import { useLayoutEffect, useRef, type RefObject } from "react";
import {
	clearReveal,
	commonPrefixLength,
	nextRevealLength,
	paintReveal,
	revealableText,
	revealableTextNodes,
	revealEnd,
} from "@/lib/streaming-reveal";

function prefersReducedMotion(): boolean {
	return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/**
 * rootRef 内の描画済みテキストを、text が伸びるたびに先頭から少しずつ見せる。
 * 描画済みの文字が前回と食い違ったら (再試行・同じジョブの次の応答・Markdown の解釈の変化)、
 * 食い違った位置から見せ直す。
 */
export function useStreamingReveal(rootRef: RefObject<HTMLElement | null>, text: string): void {
	const renderedRef = useRef("");
	const shownRef = useRef(0);
	/** ここより手前は見せきった文字。帯をかけ直さない。 */
	const settledRef = useRef(0);
	const nodesRef = useRef<Text[]>([]);
	const totalRef = useRef(0);
	const frameRef = useRef(0);

	// 描画前に塗る。useEffect だと、届いたまとまりが 1 フレームだけ全部見えてから消える。
	useLayoutEffect(() => {
		const root = rootRef.current;
		if (root === null) return;
		nodesRef.current = revealableTextNodes(root);
		const rendered = revealableText(nodesRef.current);
		const kept = commonPrefixLength(renderedRef.current, rendered);
		renderedRef.current = rendered;
		totalRef.current = rendered.length;
		// 帯が末尾を抜けている途中に届いた文字は、帯の中からでなく隠した状態から始める。
		// 帯に残っていた手前の文字は見せきる (薄く戻すとちらつく)。
		if (shownRef.current > kept) {
			shownRef.current = kept;
			settledRef.current = kept;
		}
		if (prefersReducedMotion()) shownRef.current = revealEnd(totalRef.current);

		// 帯が末尾を抜けきったら、次のまとまりは今の末尾から始める。
		const paint = () => {
			const painting = paintReveal(
				nodesRef.current,
				totalRef.current,
				shownRef.current,
				settledRef.current,
			);
			if (!painting) {
				shownRef.current = totalRef.current;
				settledRef.current = shownRef.current;
			}
			return painting;
		};

		let last = performance.now();
		const tick = (now: number) => {
			shownRef.current = nextRevealLength(
				shownRef.current,
				revealEnd(totalRef.current),
				now - last,
			);
			last = now;
			frameRef.current = paint() ? requestAnimationFrame(tick) : 0;
		};
		if (paint() && frameRef.current === 0) frameRef.current = requestAnimationFrame(tick);
	}, [rootRef, text]);

	useLayoutEffect(
		() => () => {
			cancelAnimationFrame(frameRef.current);
			frameRef.current = 0;
			clearReveal();
		},
		[],
	);
}
